import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { ThemeContext } from 'styled-components';
// Lightweight throttle implementation to avoid importing entire lodash
const throttle = (func, wait) => {
    let timeout;
    let previous = 0;
    return function(...args) {
        const now = Date.now();
        const remaining = wait - (now - previous);
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            func.apply(this, args);
        } else if (!timeout) {
            timeout = setTimeout(() => {
                previous = Date.now();
                timeout = null;
                func.apply(this, args);
            }, remaining);
        }
    };
};
import Authorized from "../../../layouts/Authorized";
import LinkedAccountsModel from "../models/LinkedAccountsModel";
import GroupList from "../../common/components/GroupList";
import SectionHeader from "../../template/components/SectionHeader";

const ProfileView = () => {
    const mediaTheme = useTheme();
    const theme = useContext(ThemeContext);
    const isMediumOrLarger = useMediaQuery(mediaTheme.breakpoints.up('md'));
    const [activeTab, setActiveTab] = useState(0);
    const scrollContainerRef = useRef(null);
    const sectionRefs = useRef([]);
    const tabsRef = useRef(null);

    const sectionTitles = ["Financial Accounts", "Groups"];

    // Function to scroll to a section based on its index
    const scrollToSection = (index) => {
        const section = sectionRefs.current[index];
        if (section && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            // Account for the paddingTop (40px) to align section with top of viewport
            const topPos = section.offsetTop - container.offsetTop - 40;
            container.scrollTo({
                top: Math.max(0, topPos),
                left: 0,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const handleScroll = throttle(() => {
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;

            const containerScrollPosition = scrollContainer.scrollTop;
            const viewportHeight = scrollContainer.clientHeight;
            const scrollCenter = containerScrollPosition + viewportHeight / 2;

            // If at the very top, always select the first section
            if (containerScrollPosition <= 10) {
                setActiveTab(0);
                return;
            }

            // Find the section that is most prominently visible
            let activeIndex = 0;
            let maxVisibility = 0;
            let bestMatch = null;

            sectionRefs.current.forEach((section, index) => {
                if (!section) return;

                const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;

                // Calculate how much of the section is visible in the viewport
                const visibleTop = Math.max(sectionTop, containerScrollPosition);
                const visibleBottom = Math.min(sectionBottom, containerScrollPosition + viewportHeight);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                const visibilityRatio = visibleHeight / section.offsetHeight;

                // Check if scroll center is within this section (highest priority)
                const isScrollCenterInSection = scrollCenter >= sectionTop && scrollCenter <= sectionBottom;

                if (isScrollCenterInSection) {
                    // If scroll center is in this section, this is the active one
                    bestMatch = { index, priority: 2, visibilityRatio };
                } else if (visibilityRatio > 0.3 && (bestMatch === null || bestMatch.priority < 2)) {
                    // If no center match yet, use visibility ratio
                    if (bestMatch === null || visibilityRatio > bestMatch.visibilityRatio) {
                        bestMatch = { index, priority: 1, visibilityRatio };
                    }
                }
            });

            if (bestMatch) {
                setActiveTab(bestMatch.index);
            } else {
                // Fallback: find section closest to scroll position
                let closestIndex = 0;
                let closestDistance = Infinity;

                sectionRefs.current.forEach((section, index) => {
                    if (!section) return;
                    const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                    const distance = Math.abs(sectionTop - containerScrollPosition);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });
                setActiveTab(closestIndex);
            }
        }, 100);

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            // Call once on mount to set initial active tab
            handleScroll();
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const assignRef = (section, index) => {
        sectionRefs.current[index] = section;
    };

    // Function to scroll the selected tab into view
    const centerSelectedTab = (tabIndex) => {
        if (!tabsRef.current) return;

        // Find all tab buttons
        const tabButtons = tabsRef.current.querySelectorAll('.MuiTab-root');
        if (!tabButtons || tabButtons.length === 0) return;

        const selectedTab = tabButtons[tabIndex];
        if (!selectedTab) return;

        // Scroll the tab into view
        selectedTab.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        });
    };

    return (
        <>
            <Authorized>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        overflow: 'hidden',
                        backgroundColor: theme.colors.backgroundSecondary,
                    }}
                >
                    <Box
                        sx={{
                            // Lateral
                            width: { xs: '100%', sm: '75%', md: 'min(80%, calc(100% - 280px))' },
                            maxWidth: { xs: '100%', sm: '800px', md: 'min(800px, calc(100% - 280px))' },
                            left: { xs: '50%', sm:'54.5%', md: '53%'},
                            transform: 'translateX(-50%)',

                            top: { xs: '5vw', sm: '5vw' },
                            bottom: { xs: '5vw', sm: '0' },
                            height: { xs: 'calc(85vh - 64px)', sm: '85vh' },

                            margin: 'auto',
                            paddingLeft: { xs: 0, sm: '10px' },
                            paddingRight: { xs: 0, sm: '10px' },
                            position: 'fixed',
                            boxShadow: 20,
                            borderRadius: { xs: 0, sm: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme.colors.backgroundPrimary,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Profile Header */}
                        <SectionHeader
                            title="Profile"
                        />
                        <Tabs
                            ref={tabsRef}
                            value={activeTab}
                            onChange={(e, newValue) => {
                                setActiveTab(newValue);
                                scrollToSection(newValue);
                                // Center the selected tab - use requestAnimationFrame for better timing
                                requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                        centerSelectedTab(newValue);
                                    });
                                });
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            indicatorColor="secondary"
                            textColor="inherit"
                            sx={{
                                '.MuiTabs-flexContainer': {
                                    justifyContent: 'space-between',
                                },
                                '.MuiTab-root': {
                                    flex: 1,
                                    minWidth: '100px',
                                    maxWidth: 'none',
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent !important',
                                    transition: 'opacity 120ms ease, background-color 0ms !important',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                        backgroundColor: 'transparent !important',
                                        transition: 'opacity 120ms ease, background-color 0ms !important',
                                    },
                                    '&.Mui-selected': {
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        backgroundColor: 'transparent !important',
                                    },
                                    '& .MuiTouchRipple-root': {
                                        display: 'none !important',
                                    },
                                    '& .MuiTouchRipple-ripple': {
                                        display: 'none !important',
                                    }
                                }
                            }}
                        >
                            {sectionTitles.map((title, index) => (
                                <Tab sx={{ color: theme.colors.text }} key={index} label={title} />
                            ))}
                        </Tabs>
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', marginRight: { xs: 0, sm: '-5px' }, boxSizing: 'border-box', overflow: 'hidden' }}>
                            <Box
                                ref={scrollContainerRef}
                                sx={{
                                    overflow: 'auto',
                                    flex: 1,
                                    minHeight: 0,
                                    paddingRight: { xs: 0, sm: '5px' },
                                    paddingTop: '40px',
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: `${theme.colors.brandSecondary} !important`,
                                    },
                                    scrollbarColor: `${theme.colors.brandSecondary} transparent`,
                                }}
                            >
                                <Box
                                    key="section-financial-accounts"
                                    ref={(el) => assignRef(el, 0)}
                                    data-title={"Financial Accounts"}
                                    sx={{
                                        paddingLeft: { xs: 0, sm: '10px' },
                                        paddingRight: { xs: 0, sm: '10px' },
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                        overflow: 'visible', // Changed from 'hidden' to allow dropdown to show
                                    }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                    }}>
                                        <LinkedAccountsModel />
                                    </Box>
                                </Box>
                                <Box
                                    key="section-groups"
                                    ref={(el) => assignRef(el, 1)}
                                    data-title={"Groups"}
                                    sx={{
                                        paddingLeft: { xs: 0, sm: '10px' },
                                        paddingRight: { xs: 0, sm: '10px' },
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                    }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                    }}>
                                        <GroupList />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Authorized>
        </>
    );
};

export default ProfileView;
